/**
 * NPC Cleric Heuristic Decision Engine
 *
 * Pure function, zero external deps. Runs on free tier.
 * Priority: keep allies alive > buff > damage > dodge.
 *
 * Spell economy: prefers lowest slot that gets the job done.
 * Won't burn 3rd-level slots on single-target heals unless someone is down.
 * Tracks concentration to avoid overwrite.
 */
import { pickBark } from './npc-cleric-types';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Life Domain bonus: +2 + spell_level to healing. */
function lifeDomainBonus(spellLevel) {
    return 2 + spellLevel;
}
function hasSlot(ctx, level) {
    const key = String(level);
    const total = ctx.spellSlots.total[key] ?? 0;
    const used = ctx.spellSlots.used[key] ?? 0;
    return used < total;
}
function slotsRemaining(ctx, level) {
    const key = String(level);
    const total = ctx.spellSlots.total[key] ?? 0;
    const used = ctx.spellSlots.used[key] ?? 0;
    return Math.max(0, total - used);
}
function totalSlotsRemaining(ctx) {
    let total = 0;
    for (const lvl of ['1', '2', '3']) {
        total += slotsRemaining(ctx, parseInt(lvl));
    }
    return total;
}
/** Pick the lowest available slot at or above `minLevel`. */
function lowestSlot(ctx, minLevel) {
    for (let lvl = minLevel; lvl <= 3; lvl++) {
        if (hasSlot(ctx, lvl))
            return lvl;
    }
    return null;
}
/** Sort allies by HP% ascending (most wounded first). */
function sortByWounded(allies) {
    return [...allies].sort((a, b) => a.hpPercent - b.hpPercent);
}
// ---------------------------------------------------------------------------
// Thought derivation (GM-only narrative insight)
// ---------------------------------------------------------------------------
/**
 * Derive 1-3 narrative thought lines from combat context for GM display.
 * Pure function — no side effects, no DB, no types overhead.
 */
export function deriveNpcThoughts(ctx, decision) {
    const thoughts = [];
    // Scan ally HP percentages
    const downAllies = ctx.allies.filter(a => a.isDown);
    const criticalAllies = ctx.allies.filter(a => !a.isDown && a.hpPercent <= 0.25);
    const hurtAllies = ctx.allies.filter(a => !a.isDown && a.hpPercent < 0.6);
    if (downAllies.length > 0) {
        thoughts.push(`${downAllies[0].name} is down. Must act now.`);
    }
    else if (criticalAllies.length > 0) {
        thoughts.push(`${criticalAllies[0].name} is barely standing...`);
    }
    else if (hurtAllies.length >= 2) {
        thoughts.push(`The party is taking a beating. ${hurtAllies.length} wounded.`);
    }
    else if (hurtAllies.length === 1) {
        thoughts.push(`${hurtAllies[0].name} is hurt — keeping an eye on them.`);
    }
    // Spell slot pressure
    let totalSlots = 0;
    let usedSlots = 0;
    for (const lvl of ['1', '2', '3']) {
        totalSlots += ctx.spellSlots.total[lvl] ?? 0;
        usedSlots += ctx.spellSlots.used[lvl] ?? 0;
    }
    const remaining = totalSlots - usedSlots;
    if (remaining <= 1 && totalSlots > 0) {
        thoughts.push('Almost out of divine power. One spell left at most.');
    }
    else if (remaining <= 3 && totalSlots > 4) {
        thoughts.push('Running low on spell slots. Must be careful.');
    }
    // Explain decision reasoning in character voice
    const action = decision.action;
    const bonus = decision.bonusAction;
    if (bonus && bonus.type === 'revive') {
        thoughts.push(`Healing Word on ${bonus.targetName} first. Can't lose anyone.`);
    }
    else if (bonus && bonus.type === 'heal') {
        thoughts.push(`Healing Word on ${bonus.targetName}. ${action.spellName || 'Sacred Flame'} for the rest.`);
    }
    else if (action.type === 'heal') {
        thoughts.push(`${action.spellName} on ${action.targetName}. Hands-on healing needed.`);
    }
    else if (action.type === 'buff') {
        thoughts.push(`Time for ${action.spellName}. The jungle guides my hand.`);
    }
    else if (action.type === 'damage') {
        thoughts.push(`No one needs healing. ${action.spellName || 'Sacred Flame'} on the closest threat.`);
    }
    else if (action.type === 'dodge') {
        thoughts.push('Nothing useful to do. Stay defensive.');
    }
    return thoughts.slice(0, 3);
}
// ---------------------------------------------------------------------------
// Heuristic decision tree
// ---------------------------------------------------------------------------
export function clericHeuristicDecide(ctx) {
    const downAllies = ctx.allies.filter(a => a.isDown);
    const woundedAllies = sortByWounded(ctx.allies.filter(a => !a.isDown && a.hpPercent < 1));
    const criticalAllies = woundedAllies.filter(a => a.hpPercent <= 0.25);
    const hurtAllies = woundedAllies.filter(a => a.hpPercent < 0.6);
    const slots = totalSlotsRemaining(ctx);
    const isConcentrating = ctx.concentratingOn !== null;
    // Default fallback: Sacred Flame best target or dodge
    const sacredFlame = () => {
        if (ctx.enemies.length === 0)
            return { type: 'dodge' };
        // Target lowest HP enemy
        const target = [...ctx.enemies].sort((a, b) => a.currentHp - b.currentHp)[0];
        return {
            type: 'damage',
            spellName: 'Sacred Flame',
            spellLevel: 0,
            targetId: target.id,
            targetName: target.name,
            damageDice: '2d8', // Cleric 5 cantrip scaling
            damageType: 'radiant',
        };
    };
    const dodge = () => ({ type: 'dodge' });
    // -----------------------------------------------------------------------
    // P1: Ally at 0 HP → Healing Word (bonus action revive) + Sacred Flame
    // -----------------------------------------------------------------------
    if (downAllies.length > 0 && hasSlot(ctx, 1)) {
        const target = downAllies[0];
        const slotLvl = 1; // Use lowest slot for revive (just need to get them up)
        return {
            bonusAction: {
                type: 'revive',
                spellName: 'Healing Word',
                spellLevel: slotLvl,
                targetId: target.id,
                targetName: target.name,
                healingDice: `1d4+${4 + lifeDomainBonus(slotLvl)}`, // 1d4 + WIS(4) + Life(3)
                isBonusAction: true,
            },
            action: sacredFlame(),
            bark: pickBark('revive'),
            reasoning: `Revive ${target.name} (0 HP) with Healing Word`,
        };
    }
    // -----------------------------------------------------------------------
    // P2: Ally at ≤25% HP → Healing Word (bonus, emergency) + Sacred Flame
    //     Only if we have 3+ slots remaining (conserve resources)
    // -----------------------------------------------------------------------
    if (criticalAllies.length > 0 && hasSlot(ctx, 1) && slots >= 3) {
        const target = criticalAllies[0]; // Most wounded
        const slotLvl = 1;
        return {
            bonusAction: {
                type: 'heal',
                spellName: 'Healing Word',
                spellLevel: slotLvl,
                targetId: target.id,
                targetName: target.name,
                healingDice: `1d4+${4 + lifeDomainBonus(slotLvl)}`,
                isBonusAction: true,
            },
            action: sacredFlame(),
            bark: pickBark('heal'),
            reasoning: `Emergency heal ${target.name} (${Math.round(target.hpPercent * 100)}% HP)`,
        };
    }
    // -----------------------------------------------------------------------
    // P2b: Ally at ≤25% HP and low on slots → still Healing Word but more urgent
    // -----------------------------------------------------------------------
    if (criticalAllies.length > 0 && hasSlot(ctx, 1)) {
        const target = criticalAllies[0];
        return {
            bonusAction: {
                type: 'heal',
                spellName: 'Healing Word',
                spellLevel: 1,
                targetId: target.id,
                targetName: target.name,
                healingDice: `1d4+${4 + lifeDomainBonus(1)}`,
                isBonusAction: true,
            },
            action: sacredFlame(),
            bark: pickBark('heal'),
            reasoning: `Critical heal ${target.name} (${Math.round(target.hpPercent * 100)}% HP), slots low`,
        };
    }
    // -----------------------------------------------------------------------
    // P3: 3+ allies below 60% → Mass Healing Word (3rd level, bonus action)
    //     Only if we have a 3rd-level slot AND 2+ total slots left after
    // -----------------------------------------------------------------------
    if (hurtAllies.length >= 3 && hasSlot(ctx, 3) && slots >= 3) {
        return {
            bonusAction: {
                type: 'heal',
                spellName: 'Mass Healing Word',
                spellLevel: 3,
                targetId: undefined, // Heals up to 6 creatures
                healingDice: `1d4+${4 + lifeDomainBonus(3)}`, // Per creature
                isBonusAction: true,
            },
            action: sacredFlame(),
            bark: pickBark('heal'),
            reasoning: `${hurtAllies.length} allies hurt, Mass Healing Word`,
        };
    }
    // -----------------------------------------------------------------------
    // P4: Ally below 50% → Cure Wounds (touch, action, bigger heal)
    //     Only burns slot if ally is below 50% and we have 2+ slots
    // -----------------------------------------------------------------------
    const belowHalf = woundedAllies.filter(a => a.hpPercent < 0.5);
    if (belowHalf.length > 0 && slots >= 2) {
        const target = belowHalf[0]; // Most wounded (already sorted)
        const slotLvl = lowestSlot(ctx, 1);
        const diceCount = slotLvl; // Cure Wounds: 1d8 per slot level
        return {
            action: {
                type: 'heal',
                spellName: 'Cure Wounds',
                spellLevel: slotLvl,
                targetId: target.id,
                targetName: target.name,
                healingDice: `${diceCount}d8+${4 + lifeDomainBonus(slotLvl)}`,
            },
            bark: pickBark('heal'),
            reasoning: `Cure Wounds on ${target.name} (${Math.round(target.hpPercent * 100)}% HP)`,
        };
    }
    // -----------------------------------------------------------------------
    // P5: Not concentrating, 2+ enemies → Spirit Guardians (concentration)
    //     Only if we have a 3rd-level slot AND 3+ total slots (save some for heals)
    // -----------------------------------------------------------------------
    if (!isConcentrating && ctx.enemies.length >= 2 && hasSlot(ctx, 3) && slots >= 4) {
        return {
            action: {
                type: 'buff',
                spellName: 'Spirit Guardians',
                spellLevel: 3,
                damageDice: '3d8',
                damageType: 'radiant',
                isConcentration: true,
            },
            bark: pickBark('buff'),
            reasoning: `${ctx.enemies.length} enemies, cast Spirit Guardians`,
        };
    }
    // -----------------------------------------------------------------------
    // P6: Not concentrating → Bless (concentration, 1st-level)
    //     Only if 3+ allies alive and we have slots to spare
    // -----------------------------------------------------------------------
    if (!isConcentrating && ctx.allies.filter(a => !a.isDown).length >= 2 && hasSlot(ctx, 1) && slots >= 3) {
        return {
            action: {
                type: 'buff',
                spellName: 'Bless',
                spellLevel: 1,
                isConcentration: true,
            },
            bark: pickBark('buff'),
            reasoning: 'No concentration, Bless the party',
        };
    }
    // -----------------------------------------------------------------------
    // P7: Enemies alive → Sacred Flame
    // -----------------------------------------------------------------------
    if (ctx.enemies.length > 0) {
        return {
            action: sacredFlame(),
            bark: pickBark('damage'),
            reasoning: 'No healing needed, Sacred Flame',
        };
    }
    // -----------------------------------------------------------------------
    // P8: Nothing useful → Dodge
    // -----------------------------------------------------------------------
    return {
        action: dodge(),
        reasoning: 'Nothing to do, Dodge',
    };
}
//# sourceMappingURL=npc-cleric-heuristic.js.map